export type DesktopSoundPreference = {
  soundEnabled: boolean;
  volume: number;
};

const STORAGE_KEY = "neptuneDesktopSoundPreferenceV1";
const COOKIE_KEY = "neptune_desktop_sound_preference";
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
const DEFAULT_PREFERENCE: DesktopSoundPreference = {
  soundEnabled: true,
  volume: 1,
};

const normalizedPreference = (value: unknown): DesktopSoundPreference | null => {
  if (!value || typeof value !== "object") return null;
  const entry = value as Record<string, unknown>;
  const volume = Number(entry.volume);
  if (!Number.isFinite(volume)) return null;
  return {
    soundEnabled: entry.soundEnabled !== false,
    volume: Math.max(0, Math.min(volume, 1)),
  };
};

const parsePreference = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    return normalizedPreference(JSON.parse(value));
  } catch {
    return null;
  }
};

const readCookiePreference = () => {
  if (typeof document === "undefined") return null;
  const encoded = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${COOKIE_KEY}=`))
    ?.slice(COOKIE_KEY.length + 1);
  if (!encoded) return null;
  try {
    return parsePreference(decodeURIComponent(encoded));
  } catch {
    return null;
  }
};

export function readDesktopSoundPreference(): DesktopSoundPreference {
  if (typeof window !== "undefined") {
    try {
      const stored = parsePreference(window.localStorage.getItem(STORAGE_KEY));
      if (stored) return stored;
    } catch {
      // Cookie remains available when browser storage is disabled.
    }
  }
  return readCookiePreference() || { ...DEFAULT_PREFERENCE };
}

export function writeDesktopSoundPreference(
  preference: DesktopSoundPreference,
): DesktopSoundPreference {
  const normalized = normalizedPreference(preference) || { ...DEFAULT_PREFERENCE };
  const serialized = JSON.stringify(normalized);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
      // Cookie is the fallback when browser storage is disabled.
    }
  }
  if (typeof document !== "undefined") {
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(serialized)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
  }
  return normalized;
}
